import { POST_LENGTHS, WORDPRESS_POST_LENGTHS } from "./constants/postSettings";

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ref } from "firebase/database";
import { db } from "./firebase/config";
import { FiEye, FiTrash2 } from "react-icons/fi";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import {
  isLocalProductAutoPostConfig,
  removePostConfigWithCronIndex,
} from "./utils/rtdbCronIndex";

const isTopicConfigCompleted = (config = {}) => {
  const topicEntries = Object.values(config?.topics || {});
  return (
    topicEntries.length > 0 &&
    topicEntries.every(
      (topic) => String(topic?.postStatus || "").toLowerCase() === "posted"
    )
  );
};

const isProductConfigCompleted = (config = {}) => {
  if (config?.allProductsPostedAt) {
    return true;
  }

  const products = Array.isArray(config?.products)
    ? config.products.filter(Boolean)
    : [];
  if (products.length === 0) {
    return false;
  }

  const postingState =
    config?.productPostingState &&
    typeof config.productPostingState === "object" &&
    !Array.isArray(config.productPostingState)
      ? config.productPostingState
      : {};

  return products.every((product, index) => {
    const productId =
      product?.id !== undefined && product?.id !== null
        ? String(product.id).trim()
        : "";
    const productUrl = String(product?.permalink || "")
      .trim()
      .toLowerCase();
    const productName = String(product?.name || "")
      .trim()
      .toLowerCase();

    const candidateKeys = [];
    if (productId) {
      candidateKeys.push(
        `product_id_${productId}`,
        productId,
        `product_id_${encodeURIComponent(productId)}`
      );
    }
    if (productUrl) {
      candidateKeys.push(`product_url_${encodeURIComponent(productUrl)}`);
    }
    if (productName) {
      candidateKeys.push(`product_name_${encodeURIComponent(productName)}`);
    }
    candidateKeys.push(`product_index_${index}`);

    return candidateKeys.some((key) => {
      const state = postingState?.[key];
      return String(state?.postStatus || state?.status || "").toLowerCase() === "posted";
    });
  });
};

const isConfigExhausted = (config = {}) => {
  return config?.inputType === "products"
    ? isProductConfigCompleted(config)
    : isTopicConfigCompleted(config);
};

const AutoPostConfigScreen = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // "all", "active", "inactive"
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isPlatformDropdownOpen, setIsPlatformDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);
  const platformDropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchConfigs = React.useCallback(async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const { get } = await import("firebase/database");
      const configsRef = ref(db, `/posts/${userId}/`);
      const snapshot = await get(configsRef);
      const configList = [];
      snapshot.forEach((childSnapshot) => {
        const config = { id: childSnapshot.key, ...childSnapshot.val() };
        if (!isLocalProductAutoPostConfig(config)) {
          configList.push(config);
        }
      });
      configList.sort((a, b) => {
        const timeA = a.time
          ? new Date(`1970-01-01T${a.time}:00Z`)
          : new Date();
        const timeB = b.time
          ? new Date(`1970-01-01T${b.time}:00Z`)
          : new Date();
        return timeA - timeB;
      });
      setConfigs(configList);
    } catch (error) {
      console.error("Error fetching configs:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setIsStatusDropdownOpen(false);
      }
      if (
        platformDropdownRef.current &&
        !platformDropdownRef.current.contains(event.target)
      ) {
        setIsPlatformDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const deleteConfig = async (configId) => {
    confirmAlert({
      title: "Confirm to delete",
      message: "Are you sure you want to delete this configuration?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              const userId = localStorage.getItem("userId");
              await removePostConfigWithCronIndex(db, userId, configId);
              fetchConfigs(); // Refresh list after delete
            } catch (error) {
              console.error("Error deleting config:", error);
            }
          },
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const getPostLengthLabel = (postLength, platform) => {
    // Tìm kiếm trong mảng phù hợp dựa trên platform
    const lengthsArray =
      platform === "wordpress" ? WORDPRESS_POST_LENGTHS : POST_LENGTHS;
    const foundLength = lengthsArray.find(
      (length) => length.value === postLength
    );

    // Nếu không tìm thấy trong mảng chính, thử tìm trong mảng còn lại
    if (!foundLength) {
      const alternativeArray =
        platform === "wordpress" ? POST_LENGTHS : WORDPRESS_POST_LENGTHS;
      const alternativeLength = alternativeArray.find(
        (length) => length.value === postLength
      );
      return alternativeLength ? alternativeLength.label : "Chưa thiết lập";
    }

    return foundLength.label;
  };

  const openFanpage = (fanpageID) => {
    window.open(
      `https://www.facebook.com/profile.php?id=${fanpageID}`,
      "_blank"
    );
  };

  const getPlatformLabel = (platformValue) => {
    switch (platformValue) {
      case "facebook": return "Facebook";
      case "zalo": return "Zalo";
      case "wordpress": return "Website";
      default: return platformValue;
    }
  };

  const getAvailablePlatforms = () => {
    const platforms = [...new Set(configs.map((c) => c.platform || "facebook"))];
    return platforms.filter(Boolean);
  };

  const getConfigTitle = (config) => {
    if (config.inputType === "products") {
      if (config.customInfo?.trim()) {
        return config.customInfo.trim();
      }

      const validProducts = Array.isArray(config.products)
        ? config.products.filter((product) => product?.name)
        : [];

      if (validProducts.length === 1) {
        return validProducts[0].name;
      }

      if (validProducts.length > 1) {
        return `${validProducts.length} sản phẩm WooCommerce`;
      }

      return "Thiết lập tự động từ sản phẩm";
    }

    return config.prompt || "Cấu hình tự động";
  };

  const getConfigSearchText = (config) => {
    const productNames = Array.isArray(config.products)
      ? config.products
          .map((product) => product?.name || "")
          .filter(Boolean)
          .join(" ")
      : "";

    return [
      config.prompt,
      config.customInfo,
      config.fanpageName,
      config.wordpressSiteName,
      config.websiteUrl,
      productNames,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
  };

  const filteredConfigs = configs.filter((config) => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !normalizedSearchTerm ||
      getConfigSearchText(config).includes(normalizedSearchTerm);

    const matchesStatus =
      filterStatus === "all"
        ? true
        : filterStatus === "active"
        ? config.autoPostEnabled
        : filterStatus === "inactive"
        ? !config.autoPostEnabled
        : true;

    const configPlatform = config.platform || "facebook";
    const matchesPlatform =
      selectedPlatform === "all" ? true : configPlatform === selectedPlatform;

    return matchesSearch && matchesStatus && matchesPlatform;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-4 md:px-8 md:py-8">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6 md:mb-8 text-center">
          THIẾT LẬP VIẾT & ĐĂNG BÀI TỰ ĐỘNG ĐÃ LƯU
        </h1>

        <div className="mb-6 space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 md:flex md:flex-row md:gap-4">
          <div className="sm:col-span-2 md:flex-1">
            <input
              type="text"
              placeholder="Tìm theo nội dung, sản phẩm, fanpage hoặc website..."
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="md:w-40 relative" ref={statusDropdownRef}>
            <button
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              className={`w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center ${
                filterStatus !== "all" ? "bg-white" : "bg-gray-50"
              }`}
            >
              <span>
                {filterStatus === "all"
                  ? "Trạng thái"
                  : filterStatus === "active"
                  ? "Đang chạy"
                  : "Đã tắt"}
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${
                  isStatusDropdownOpen ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            {isStatusDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-2">
                <button
                  onClick={() => {
                    setFilterStatus("all");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Trạng thái
                </button>
                <button
                  onClick={() => {
                    setFilterStatus("active");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Đang chạy
                </button>
                <button
                  onClick={() => {
                    setFilterStatus("inactive");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Đã tắt
                </button>
              </div>
            )}
          </div>

          <div className="md:w-40 relative" ref={platformDropdownRef}>
            <button
              onClick={() => setIsPlatformDropdownOpen(!isPlatformDropdownOpen)}
              className={`w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center ${
                selectedPlatform !== "all" ? "bg-white" : "bg-gray-50"
              }`}
            >
              <span className="truncate">
                {selectedPlatform === "all" ? "Nền tảng" : getPlatformLabel(selectedPlatform)}
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${
                  isPlatformDropdownOpen ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            {isPlatformDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-2">
                <button
                  onClick={() => {
                    setSelectedPlatform("all");
                    setIsPlatformDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Tất cả
                </button>
                {getAvailablePlatforms().map((platform) => (
                  <button
                    key={platform}
                    onClick={() => {
                      setSelectedPlatform(platform);
                      setIsPlatformDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100"
                  >
                    {getPlatformLabel(platform)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {filteredConfigs.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            {configs.length === 0
              ? "Không có cấu hình nào được tạo. Bạn có thể tạo cấu hình mới"
              : "Không tìm thấy kết quả phù hợp với điều kiện tìm kiếm"}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredConfigs.map((item) => {
              const exhausted = isConfigExhausted(item);

              return (
                <div
                  key={item.id}
                  onClick={() =>
                    navigate("/auto-post", { state: { config: item } })
                  }
                  className="bg-white rounded-xl shadow-lg overflow-hidden transform transition duration-300 hover:scale-105 cursor-pointer"
                >
                  <div className="p-6">
                    <div className="flex justify-between items-start gap-3 mb-4">
                      <h3 className="font-bold text-xl text-gray-800 line-clamp-2">
                        {getConfigTitle(item)}
                      </h3>
                      {exhausted && (
                        <span className="inline-flex shrink-0 items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                          Đã đăng hết
                        </span>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center text-gray-600">
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                        </svg>
                        <span className="text-sm">
                          {item.inputType === "products"
                            ? `${item.platform === "zalo" ? "Zalo OA" : item.platform === "wordpress" ? "Website" : "Facebook"} • Từ sản phẩm${
                                Array.isArray(item.products) &&
                                item.products.length > 0
                                  ? ` • ${item.products.length} sản phẩm`
                                  : ""
                              }`
                            : item.platform === "wordpress"
                            ? "Website • Từ chủ đề"
                            : item.platform === "zalo"
                            ? "Zalo OA • Từ chủ đề"
                            : "Facebook • Từ chủ đề"}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-gray-600">
                        <div className="flex items-center">
                          <svg
                            className="w-5 h-5 mr-2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                          <span>
                            {item.scheduleMode === "fixed-dates"
                              ? item.fixedDateSchedules &&
                                item.fixedDateSchedules.length > 0
                                ? `${item.fixedDateSchedules.length} lịch cố định`
                                : "Chưa có lịch"
                              : Array.isArray(item.times) && item.times.length > 0
                              ? item.times.join(" | ")
                              : item.time
                              ? item.time
                              : "Chưa đặt giờ"}
                          </span>
                        </div>
                        <div
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            item.autoPostEnabled
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {item.autoPostEnabled ? "🔵 Đang chạy" : "⚪ Đã tắt"}
                        </div>
                      </div>

                      {/* Schedule Mode Badge */}
                      {item.scheduleMode && (
                        <div className="flex items-center text-gray-600">
                          <svg
                            className="w-5 h-5 mr-2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                          <span className="text-sm">
                            {item.scheduleMode === "fixed-dates"
                              ? "📅 Ngày cố định"
                              : "🔄 Lặp lại hàng tuần"}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center text-gray-600">
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M4 6h16M4 12h16M4 18h7"></path>
                        </svg>
                        <span>
                          {getPostLengthLabel(item.postLength, item.platform)}
                        </span>
                      </div>

                      <div className="flex items-center text-gray-600">
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                        </svg>
                        <span>{item.postStyles || "Chưa chọn phong cách"}</span>
                      </div>

                      {item.platform === "wordpress" ? (
                        <div className="flex items-center text-gray-600">
                          <svg
                            className="w-5 h-5 mr-2 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                            ></path>
                          </svg>
                          <span
                            className="text-blue-500 hover:text-blue-700 truncate cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.websiteUrl) {
                                let url = item.websiteUrl;
                                if (!url.match(/^https?:\/\//i)) {
                                  url = "https://" + url;
                                }
                                window.open(url, "_blank");
                              }
                            }}
                            title={
                              item.wordpressSiteName ||
                              item.websiteUrl ||
                              "Chưa có website"
                            }
                          >
                            {item.wordpressSiteName ||
                              item.websiteUrl ||
                              "Chưa có website"}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center text-gray-600">
                          <svg
                            className="w-5 h-5 mr-2 flex-shrink-0"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                          </svg>
                          {(() => {
                            // Zalo: lấy tên từ selectedAccounts vì fanpageName bị rỗng
                            const displayName = item.platform === "zalo"
                              ? (Array.isArray(item.selectedAccounts) && item.selectedAccounts.length > 0
                                  ? item.selectedAccounts.map(a => a.name).filter(Boolean).join(", ")
                                  : null)
                                || item.fanpageName
                                || "Chưa chọn Zalo OA"
                              : item.fanpageName || "Chưa chọn fanpage";
                            const isZalo = item.platform === "zalo";
                            return (
                              <span
                                className={`${isZalo ? "text-gray-700" : "text-blue-500 hover:text-blue-700 cursor-pointer"} truncate`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isZalo && item.fanpageID) {
                                    openFanpage(item.fanpageID);
                                  }
                                }}
                                title={displayName}
                              >
                                {displayName}
                              </span>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoPostConfigScreen;
